import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface ServiceItemType {
  id: number
  name: string
  price: number | null
  category: number
  category_label: string
  status: number
  usage_count: number
}

interface Props {
  service_item_types: ServiceItemType[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/service_item_types" },
  { title: "Service Items", href: "/admin/service_item_types" },
]

function formatCurrency(amount: number): string {
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export default function Index({ service_item_types }: Props) {
  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this service item?")) {
      router.delete(`/admin/service_item_types/${id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Service Items" />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Service Items</h1>
          <Link
            href="/admin/service_item_types/new"
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Service Item
          </Link>
        </div>

        <p className="text-hpi-red mb-4 text-sm font-medium">
          Don&apos;t re-use service items — changing them will affect your case
          history.
        </p>

        {service_item_types.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Name
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-right">
                  Price
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {service_item_types.map((sit) => (
                <tr
                  key={sit.id}
                  className={`hover:bg-hpi-pale-blue ${sit.status === 1 ? "text-hpi-med-gray italic" : ""}`}
                >
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {sit.name}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-right">
                    {sit.price && sit.price > 0
                      ? formatCurrency(sit.price)
                      : ""}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {sit.category_label}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/admin/service_item_types/${sit.id}/edit`}
                      className="text-hpi-active-blue mr-2 hover:underline"
                    >
                      Edit
                    </Link>
                    {sit.usage_count === 0 && (
                      <button
                        onClick={() => handleDelete(sit.id)}
                        className="text-hpi-red hover:underline"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No service items found.</p>
        )}
      </div>
    </AppLayout>
  )
}
